import "./style.css";
import React, { useEffect, useRef, useState, useContext } from "react";
import { getQRproducts } from "../../../network/network";
import QRmenu from "./QRmenu";
import QRmenuWithVoice from "./QRmenu_with_voice";
import { Button, App, Empty, Spin } from "antd";
import { AppContext } from "../../../context/AppContextProvider";
import { useTranslation } from "react-i18next";
import { handleApiError } from "../../../utils/errorHandler";
import {
  QR_LANGUAGES,
  applyQrLanguage,
  readStoredQrLocale,
  restoreQrLanguage,
} from "./qrLanguage";
import {
  clearCartIfMenuChanged,
  clearQrCart,
  getQrContextKey,
  getQrMenuProductIds,
  saveQrMenuProductIds,
} from "./qrCartStorage";

const wrapperStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  background:
    "linear-gradient(145deg, var(--color-primary-soft), var(--color-white))",
};

const cardStyle = {
  width: "100%",
  maxWidth: "420px",
  background: "var(--color-white)",
  borderRadius: "24px",
  boxShadow: "0 18px 40px var(--color-shadow-soft)",
  padding: "24px 20px 20px",
  textAlign: "center",
};

const logoImageStyle = {
  width: "100px",
  height: "100px",
  borderRadius: "50%",
  objectFit: "cover",
  boxShadow: "0 6px 18px var(--color-shadow-soft)",
};

const pillButtonBase = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};

const textLinkButtonStyle = {
  marginTop: "16px",
};

const CenteredCard = ({ children }) => (
  <div style={wrapperStyle}>
    <div style={cardStyle}>{children}</div>
  </div>
);

const StoreLogo = ({ logo, name }) => (
  <div style={{ marginBottom: "16px" }}>
    <img src={logo} alt={name} style={logoImageStyle} />
  </div>
);

const QRorderingContent = () => {
  const [products, setProducts] = useState([]);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1 = welcome, 2 = choose order type, 3 = actual menu
  const [step, setStep] = useState(1);
  const [selectedMode, setSelectedMode] = useState(null); // "ai" | "manual" | null

  const { t } = useTranslation("qr");
  const { setAppConfigWrapper } = useContext(AppContext);
  const initializationStartedRef = useRef(false);
  const setAppConfigWrapperRef = useRef(setAppConfigWrapper);
  const translationRef = useRef(t);

  translationRef.current = t;

  // -------- Load products / company details --------
  useEffect(() => {
    if (initializationStartedRef.current) return;
    initializationStartedRef.current = true;

    const search = window.location.search;
    if (!search) {
      setLoading(false);
      return;
    }

    const params = new URLSearchParams(search);
    const src = params.get("src");
    const table = params.get("table");
    const isDriveThru = params.get("is_drive_thru");

    if (!src || !table || isDriveThru === null) {
      setLoading(false);
      return;
    }

    // Read last stored QR before overwriting. LocalStorage is browser/domain level,
    // so store/table changes must not carry the previous QR cart.
    const lastQrContext = getQrContextKey({
      src: localStorage.getItem("src"),
      table: localStorage.getItem("table"),
      isDriveThru: localStorage.getItem("is_drive_thru"),
    });
    const currentQrContext = getQrContextKey({ src, table, isDriveThru });
    const isSameQR = lastQrContext === currentQrContext;

    // If different store/table/QR mode => clear data for that QR
    if (!isSameQR) {
      localStorage.removeItem("src");
      localStorage.removeItem("table");
      localStorage.removeItem("is_drive_thru");
      localStorage.removeItem("lang");
      clearQrCart();
      localStorage.removeItem("change_mode"); // clear saved mode for new store
    }

    // Store current QR params (always)
    localStorage.setItem("src", src);
    localStorage.setItem("table", table);
    localStorage.setItem("is_drive_thru", isDriveThru);

    // Decide which step to go to based on stored language + mode
    const storedLocale = readStoredQrLocale(); // "en"|"ar"|"fr"|"es"|null
    const storedMode = localStorage.getItem("change_mode"); // "ai" | "manual" | null

    if (storedLocale) {
      restoreQrLanguage(setAppConfigWrapperRef.current);

      if (storedMode === "ai" || storedMode === "manual") {
        // language + mode already selected -> go directly to menu
        setSelectedMode(storedMode);
        setStep(3);
      } else {
        // language selected but no mode -> show mode screen
        setStep(2);
      }
    } else {
      // no language yet -> show language screen
      setStep(1);
    }

    getQRproducts({ src, table })
      .then((response) => {
        const data = response.data || response.response?.data;
        if (data?.success) {
          const categoriesProducts = data.data.categories_products || [];
          const menuProductIds = getQrMenuProductIds(categoriesProducts);

          saveQrMenuProductIds(menuProductIds);
          clearCartIfMenuChanged(menuProductIds);
          setProducts(categoriesProducts);
          setCompanyDetails(data.data || null);
        } else {
          handleApiError(response, translationRef.current);
        }
      })
      .catch((error) => handleApiError(error, translationRef.current))
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // -------- Language helpers (Step 1 buttons only) --------

  const handleLanguageSelectStep1 = (langCode) => {
    // langCode: "en" | "ar" | "fr" | "es"
    applyQrLanguage(langCode, setAppConfigWrapper);

    // After choosing language, decide where to go based on stored mode
    const storedMode = localStorage.getItem("change_mode");
    if (storedMode === "ai" || storedMode === "manual") {
      setSelectedMode(storedMode);
      setStep(3);
    } else {
      setStep(2);
    }
  };

  // -------- Change Mode handlers --------

  const handleOrderMode = (mode) => {
    // mode: "ai" | "manual"
    setSelectedMode(mode);
    localStorage.setItem("change_mode", mode);
    setStep(3);
  };

  const handleBackToOrderMode = () => {
    // user explicitly wants to change mode -> clear saved mode
    localStorage.removeItem("change_mode");
    setSelectedMode(null);
    setStep(2);
  };

  // -------- UI: Step 1 (Welcome screen) --------

  const renderWelcomeScreen = () => {
    const name = companyDetails?.company_name || t("our_store");
    const logo = companyDetails?.logo || "/images/image-not-found.png";

    return (
      <CenteredCard>
        <StoreLogo logo={logo} name={name} />

        <h2
          style={{
            fontSize: "22px",
            margin: "0 0 8px",
            fontWeight: 700,
            color: "var(--color-heading)",
          }}
        >
          {t("welcome_to")}
        </h2>
        <h3
          style={{
            fontSize: "18px",
            margin: 0,
            fontWeight: 600,
            color: "var(--color-primary)",
            textTransform: "capitalize",
          }}
        >
          {name}
        </h3>

        <div
          style={{
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: "1px dashed var(--color-border)",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "var(--color-text-muted)",
              marginBottom: "12px",
            }}
          >
            {t("select_language")}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              justifyContent: "center",
            }}
          >
            {QR_LANGUAGES.map((lang) => (
              <Button
                key={lang.code}
                type="default"
                htmlType="button"
                onClick={() => handleLanguageSelectStep1(lang.code)}
                style={{
                  ...pillButtonBase,
                  maxWidth: "150px",
                  flex: "1 1 40%",
                }}
              >
                {lang.img ? (
                  <img
                    src={lang.img}
                    alt={lang.label}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      boxShadow: "0 6px 18px var(--color-shadow-soft)",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "26px",
                      background: "var(--color-bg)",
                      boxShadow: "0 6px 18px var(--color-shadow-soft)",
                    }}
                  >
                    {lang.emoji}
                  </span>
                )}
                <span>{lang.label}</span>
              </Button>
            ))}
          </div>

          <div
            style={{
              fontSize: "11px",
              color: "var(--color-text-muted)",
              marginTop: "18px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {t("powered_by")}{" "}
            <a
              href="https://www.anypos.ai/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--color-primary)",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              AnyPOS
            </a>
          </div>
        </div>
      </CenteredCard>
    );
  };

  // -------- UI: Step 2 (Change Mode screen) --------

  const renderOrderModeScreen = () => {
    const name = companyDetails?.company_name || t("our_store");
    const logo = companyDetails?.logo || "/images/image-not-found.png";

    return (
      <CenteredCard>
        <StoreLogo logo={logo} name={name} />

        <h3
          style={{
            fontSize: "18px",
            margin: 0,
            fontWeight: 600,
            color: "var(--color-primary)",
            textTransform: "capitalize",
            marginBottom: "16px",
          }}
        >
          {name}
        </h3>

        <h2
          style={{
            fontSize: "20px",
            margin: "0 0 6px",
            fontWeight: 700,
            color: "var(--color-heading)",
          }}
        >
          {t("how_would_you_like_to_order")}
        </h2>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginTop: 8,
          }}
        >
          {/* Order with AI */}
          <Button
            type="default"
            htmlType="button"
            onClick={() => handleOrderMode("ai")}
            style={pillButtonBase}
          >
            <img
              src="/icons/voice-assistant.png"
              alt={t("voice_assistant")}
              style={{
                width: "50px",
                height: "50px",
              }}
            />
            <span>{t("order_with_ai")}</span>
          </Button>

          {/* Order yourself */}
          <Button
            type="default"
            htmlType="button"
            onClick={() => handleOrderMode("manual")}
            style={pillButtonBase}
          >
            <img
              src="/icons/touch-screen.png"
              alt={t("touch_screen")}
              style={{
                width: "50px",
                height: "50px",
              }}
            />
            <span>{t("order_yourself")}</span>
          </Button>
        </div>

        {/* Bottom button: go back to Step 1 (language selection) */}
        <Button
          type="link"
          htmlType="button"
          onClick={() => setStep(1)}
          style={textLinkButtonStyle}
        >
          {t("select_language")}
        </Button>
      </CenteredCard>
    );
  };

  // -------- Main render --------

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!products.length || !companyDetails) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          textAlign: "center",
        }}
      >
        <Empty
          description={
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                {t("no_menu_data_found")}
              </div>
              <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
                {t("please_scan_valid_qr_code_or_try_again_later")}
              </div>
            </div>
          }
        />
      </div>
    );
  }

  if (step === 1) {
    return renderWelcomeScreen();
  }

  if (step === 2) {
    return renderOrderModeScreen();
  }

  // step === 3: show actual menu
  const commonMenuProps = {
    categoriesProducts: products,
    companyDetails,
    onBackToOrderMode: handleBackToOrderMode,
  };

  if (selectedMode === "ai") {
    return <QRmenuWithVoice {...commonMenuProps} />;
  }

  // default to manual if anything else
  return <QRmenu {...commonMenuProps} />;
};

const QRordering = () => (
  <App>
    <QRorderingContent />
  </App>
);

export default QRordering;
